import { useState } from 'react'
import { Search, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { ISBT_LABELS } from '@/lib/isbtCatalog'
import {
  DATE_RANGE_PRESETS,
  customRange,
  presetForRange,
  rangeForPreset,
  type DateRangePreset,
} from '@/lib/dateRangePresets'
import { statusLabel } from '@/lib/productCodeStatus'
import { CATALOG_FILTER_FIELDS, CATALOG_STATUSES } from '@/types/products'
import type {
  CatalogFilterField,
  CatalogStatus,
  ProductCatalogFacets,
  ProductCatalogFilters,
} from '@/types/products'
import { cn } from '@/lib/utils'

/**
 * Search + filter controls for the ISBT 128 catalog, shared by Configure →
 * Products and the Create/Receive Product picker so the two can never disagree
 * on what is filterable.
 *
 * The dropdown options come from the server (`facets`), narrowed by every OTHER
 * active filter. The picker used to derive them by scanning the whole catalog
 * client-side, which was fine for a handful of curated rows and is not for
 * 14,911.
 *
 * `SearchableSelect` rather than a plain `Select`: anticoagulant alone has 62
 * distinct values, which is past the point where scrolling a list beats typing,
 * and it caps the rendered list so a long one stays fast.
 */

/**
 * The "no filter" option's value.
 *
 * It is never STORED — picking it clears the filter, so the control falls back
 * to showing its placeholder (the column name). Storing it would leave every
 * unfiltered dropdown reading "All", and a row of nine identical triggers tells
 * you nothing about what each one filters.
 */
const ALL = '__all__'

/**
 * How the tenant's Active / Inactive (z8nrz7e72t) is offered.
 *
 * `filter` (Configure → Products): an All / Active / Inactive dropdown, beside
 * the standard's own "In use only". `picker` (Create / Receive / Change): the
 * table shows Active codes only, and "Search inactive" flips it to the Inactive
 * ones — to find a code, not to use it; the picker will not select one.
 */
export type CatalogStatusMode = 'filter' | 'picker'

/** The filters a "Clear" returns to — the picker never leaves Active-only. */
export function clearedCatalogFilters(
  mode: CatalogStatusMode,
  pageSize: number | undefined,
): ProductCatalogFilters {
  return mode === 'picker' ? { page: 1, pageSize, status: 'active' } : { page: 1, pageSize }
}

export function CatalogFilterBar({
  filters,
  facets,
  onChange,
  searchValue,
  onSearchChange,
  statusMode = 'filter',
}: {
  filters: ProductCatalogFilters
  facets: ProductCatalogFacets | undefined
  onChange: (next: ProductCatalogFilters) => void
  /** Held by the parent so typing stays responsive while the query debounces. */
  searchValue: string
  onSearchChange: (value: string) => void
  statusMode?: CatalogStatusMode
}) {
  const preset = presetForRange(filters)
  // Kept locally rather than derived: a half-entered range ("from" picked, "to"
  // still empty) is a valid thing to be in the middle of, and rebuilding the
  // inputs from the applied filter would fight the person typing.
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const [showCustom, setShowCustom] = useState(preset === 'custom')

  const activeCount =
    CATALOG_FILTER_FIELDS.filter(key => filters[key]).length +
    (filters.createdFrom || filters.createdTo ? 1 : 0) +
    (filters.inUseOnly ? 1 : 0) +
    // In the picker Active-only is the resting state, not a filter.
    (statusMode === 'picker' ? (filters.status === 'inactive' ? 1 : 0) : filters.status ? 1 : 0)

  function setFilter(key: CatalogFilterField, value: string) {
    // Changing a filter returns to page 1: staying on page 7 of a narrower
    // result set shows an empty table that looks like "no matches".
    onChange({ ...filters, [key]: value === ALL ? undefined : value, page: 1 })
  }

  function setPreset(next: DateRangePreset) {
    setShowCustom(next === 'custom')
    if (next === 'custom') {
      onChange({ ...filters, ...customRange(customFrom, customTo), page: 1 })
      return
    }
    // A preset replaces BOTH ends, so an upper bound left over from a previous
    // custom range cannot survive into it.
    onChange({ ...filters, createdFrom: undefined, createdTo: undefined, ...rangeForPreset(next), page: 1 })
  }

  function setCustom(from: string, to: string) {
    setCustomFrom(from)
    setCustomTo(to)
    onChange({ ...filters, createdFrom: undefined, createdTo: undefined, ...customRange(from, to), page: 1 })
  }

  function clearAll() {
    setShowCustom(false)
    setCustomFrom('')
    setCustomTo('')
    onChange(clearedCatalogFilters(statusMode, filters.pageSize))
  }

  return (
    <div className='space-y-2'>
      <div className='flex flex-wrap items-center gap-2'>
        <div className='relative'>
          <Search className='pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground' />
          <Input
            value={searchValue}
            onChange={e => onSearchChange(e.target.value)}
            placeholder='Search code or description…'
            className='h-8 w-64 pl-8'
            aria-label='Search product codes'
          />
        </div>

        {CATALOG_FILTER_FIELDS.map(key => {
          const active = !!filters[key]
          return (
            <SearchableSelect
              key={key}
              // '' rather than the ALL sentinel, so an unfiltered control shows
              // its placeholder — which is the column name, the only thing that
              // tells nine side-by-side dropdowns apart.
              value={filters[key] ?? ''}
              onChange={value => setFilter(key, value)}
              // The trigger shows the VALUE once one is picked, so the column it
              // belongs to is on the tooltip.
              triggerTitle={ISBT_LABELS[key]}
              buttonClassName={cn('h-8 w-44', active && 'border-primary text-primary')}
              placeholder={ISBT_LABELS[key]}
              options={[
                { value: ALL, label: `All ${ISBT_LABELS[key].toLowerCase()}` },
                ...(facets?.[key] ?? []).map(value => ({ value, label: value })),
              ]}
            />
          )
        })}

        {/* "Added when" — presets plus a custom range, rather than the single
            fixed "last 30 days" toggle this replaced. */}
        <SearchableSelect
          value={preset}
          onChange={value => setPreset(value as DateRangePreset)}
          triggerTitle='Added when'
          buttonClassName={cn('h-8 w-44', preset !== 'all' && 'border-primary text-primary')}
          placeholder='Added when'
          options={DATE_RANGE_PRESETS.map(p => ({ value: p.value, label: p.label }))}
        />

        {statusMode === 'filter' ? (
          <>
            <SearchableSelect
              value={filters.status ?? ''}
              onChange={value =>
                onChange({ ...filters, status: value === ALL ? undefined : (value as CatalogStatus), page: 1 })
              }
              triggerTitle='Status'
              buttonClassName={cn('h-8 w-36', filters.status && 'border-primary text-primary')}
              placeholder='Status'
              options={[
                { value: ALL, label: 'All statuses' },
                ...CATALOG_STATUSES.map(value => ({ value, label: statusLabel(value === 'active') })),
              ]}
            />
            <Button
              size='sm'
              variant={filters.inUseOnly ? 'default' : 'outline'}
              className='h-8'
              onClick={() => onChange({ ...filters, inUseOnly: !filters.inUseOnly, page: 1 })}
            >
              In use only
            </Button>
          </>
        ) : (
          <Button
            size='sm'
            variant={filters.status === 'inactive' ? 'default' : 'outline'}
            className='h-8'
            onClick={() =>
              onChange({ ...filters, status: filters.status === 'inactive' ? 'active' : 'inactive', page: 1 })
            }
          >
            Search inactive
          </Button>
        )}

        {activeCount > 0 && (
          <Button size='sm' variant='ghost' className='h-8' onClick={clearAll}>
            <X className='mr-1 h-3.5 w-3.5' />
            Clear {activeCount}
          </Button>
        )}
      </div>

      {showCustom && (
        <div className='flex flex-wrap items-center gap-2 text-sm'>
          <span className='text-muted-foreground'>Added between</span>
          <Input
            type='date'
            value={customFrom}
            onChange={e => setCustom(e.target.value, customTo)}
            className='h-8 w-40'
            aria-label='Added from'
          />
          <span className='text-muted-foreground'>and</span>
          <Input
            type='date'
            value={customTo}
            onChange={e => setCustom(customFrom, e.target.value)}
            className='h-8 w-40'
            aria-label='Added to'
          />
          {/* Both ends are optional — "everything since March" is a range. */}
          <span className='text-xs text-muted-foreground'>Leave either side blank for open-ended.</span>
        </div>
      )}
    </div>
  )
}
